import { forwardRef, type ReactNode } from 'react';

import { t } from '@web/i18n';
import { useSidebarCollapsed } from '@web/data/repositories';
import { IconButton, VollMark } from '@web/ui/core/atoms';
import { useMetaKeyShortcut } from '@web/ui/core/hooks';
import { twcn } from '@web/ui/core/utils';

import { SidebarIconRail, type SidebarIconRailItem } from './sidebar_icon_rail';

export interface SidebarProps {
  brandLabel: string;
  body: ReactNode;
  foot?: ReactNode;
  /** Icon-only nav rendered while collapsed; grouped, with a divider between groups. */
  collapsedItems?: SidebarIconRailItem[][];
}

/**
 * Sidebar — page rail shell. Owns the rail width, muted background,
 * trailing divider and sticky-to-screen column layout. The brand row
 * (VOLL mark + eyebrow label) is rendered internally; `body` fills
 * the remaining height with its own scroll, and the optional `foot`
 * pins itself to the bottom with a hairline divider.
 *
 * Collapsed (`useSidebarCollapsed`, toggled by the in-rail button or
 * ⌘B / Ctrl+B), it shrinks to a slim rail showing only the expand
 * control; `body` and `foot` are hidden so page content flows beside it.
 */
export function Sidebar({
  brandLabel,
  body,
  foot,
  collapsedItems,
}: Readonly<SidebarProps>): React.JSX.Element {
  const collapsed = useSidebarCollapsed((s) => s.collapsed);
  const toggle = useSidebarCollapsed((s) => s.toggle);
  useMetaKeyShortcut('b', toggle);

  return (
    <nav
      className={twcn(
        'sticky top-0 flex h-screen flex-shrink-0 flex-col border-r border-border-soft bg-muted font-sans',
        'transition-width duration-slow ease-emphasized',
        collapsed ? 'w-rail-w-collapsed' : 'w-rail-w',
      )}
    >
      {collapsed ? (
        <>
          <div className="flex justify-center px-2 pb-1.5 pt-3.5">
            <IconButton
              icon="sidebar"
              variant="ghost"
              size="sm"
              aria-label={t('home.sidebar.expandMenu')}
              title={t('home.sidebar.expandMenu')}
              onClick={toggle}
            />
          </div>
          {collapsedItems == null ? (
            <div className="flex-1" />
          ) : (
            <SidebarIconRail groups={collapsedItems} />
          )}
          {foot == null ? null : (
            <div className="mt-auto flex justify-center border-t border-border-soft p-2.5">
              {foot}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2.5 px-4 pb-5.5 pt-3.5 text-foreground">
            {/* A marca é a única peça terracota desta linha — o divisor e o rótulo
                seguem a tinta do contêiner, então a cor entra no wrapper da marca e
                não no `text-foreground` acima. */}
            <span className="inline-flex text-brand-mark">
              <VollMark height={14} />
            </span>
            <span aria-hidden className="inline-block h-3 w-px bg-border-strong/40" />
            <span className="font-mono text-2xs font-medium uppercase tracking-eyebrow-wider text-muted-foreground">
              {brandLabel}
            </span>
            <span className="ml-auto inline-flex">
              <IconButton
                icon="sidebar-simple"
                variant="ghost"
                size="xs"
                aria-label={t('home.sidebar.collapseMenu')}
                title={t('home.sidebar.collapseMenu')}
                onClick={toggle}
              />
            </span>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
            {body}
          </div>
          {foot == null ? null : (
            <div className="mt-auto flex flex-col gap-1.5 border-t border-border-soft p-2.5">
              {foot}
            </div>
          )}
        </>
      )}
    </nav>
  );
}

export type SidebarSectionProps = {
  scroll?: boolean;
  className?: string;
  children?: ReactNode;
};

export const SidebarSection = forwardRef<HTMLDivElement, Readonly<SidebarSectionProps>>(
  function SidebarSection({ scroll, className, children }, ref) {
    return (
      <div
        ref={ref}
        className={twcn(
          'flex flex-col gap-px px-2',
          scroll && 'min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-2',
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

export function SidebarDivider(): React.JSX.Element {
  return <div aria-hidden className="mx-2 my-3 border-t border-border-soft" />;
}
